import { useMemo, useState } from 'react'
import { Activity, ArrowDown, ArrowRight, ArrowUp, ShieldCheck, Smartphone, UserCog, UserRoundCheck, Watch } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { DataTable } from '@/components/shared/data-table'
import { WatchPills } from '@/components/shared/watch-pills'
import { MetricCard } from '@/components/shared/metric-card'
import { SectionCard } from '@/components/shared/section-card'
import { StatusBadge } from '@/components/shared/status-badge'
import { formatCompactNumber, formatDateTime, formatRelativeTime, toDate } from '@/lib/format'
import {
  ACTIVE_WINDOW_DAYS,
  LAST_OPENED_FIELD,
  activeSinceDate,
  describeUserActivity,
} from '@/lib/activity'
import { formatWatchLabel } from '@/lib/watch-models'
import { useCollectionCount, useCollectionRecords, useWatchOwnership } from '@/hooks'
import { useAppSettings } from '@/hooks/useAppSettings'
import { AdminProfile, UserProfile } from '@/types'

type SortKey = 'identity' | 'sync' | 'goal'
type SortDir = 'asc' | 'desc'

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'identity', label: 'Identity' },
  { key: 'sync', label: 'Sync' },
  { key: 'goal', label: 'Goal' },
]

export function UsersPage() {
  const { settings } = useAppSettings()
  const parlentWatchModels = settings.watchModels?.parlent ?? []

  // Dibekukan per hari supaya nilainya stabil sebagai bagian dari query key.
  const activeSince = useMemo(() => activeSinceDate(), [])

  const totalUsers = useCollectionCount(['users'])
  const totalAdmins = useCollectionCount(['adminUsers'])
  const activeUsers = useCollectionCount(['users'], {
    greaterThanOrEqual: [[LAST_OPENED_FIELD, activeSince]],
  })
  const watchOwnership = useWatchOwnership(parlentWatchModels)

  const users = useCollectionRecords<UserProfile>(['users'], {
    orderByField: 'updatedAt',
    limitCount: 500,
  })
  const admins = useCollectionRecords<AdminProfile>(['adminUsers'], {
    orderByField: 'updatedAt',
    limitCount: 12,
  })

  const [sortKey, setSortKey] = useState<SortKey>('sync')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const sortedUsers = useMemo(() => {
    const rows = users.data ?? []
    const factor = sortDir === 'asc' ? 1 : -1
    const compare = (a: UserProfile, b: UserProfile): number => {
      switch (sortKey) {
        case 'identity': {
          const aName = (a.displayName ?? a.email ?? a.uid ?? '').toLowerCase()
          const bName = (b.displayName ?? b.email ?? b.uid ?? '').toLowerCase()
          return aName.localeCompare(bName) * factor
        }
        case 'sync': {
          const aTime = toDate(a.lastSync)?.getTime() ?? 0
          const bTime = toDate(b.lastSync)?.getTime() ?? 0
          return (aTime - bTime) * factor
        }
        case 'goal': {
          const aGoal = (a.dailyStepGoal ?? 0) + (a.dailySleepGoal ?? 0)
          const bGoal = (b.dailyStepGoal ?? 0) + (b.dailySleepGoal ?? 0)
          return (aGoal - bGoal) * factor
        }
      }
    }
    return [...rows].sort(compare)
  }, [users.data, sortKey, sortDir])

  // Satu baris per jam fisik, diurutkan dari yang paling baru terhubung.
  const registryRows = useMemo(() => {
    const registry = watchOwnership.data?.byAddress
    if (!registry) return []

    const userLabel = new Map<string, string>()
    ;(users.data ?? []).forEach((user) => {
      const id = user.id ?? user.uid
      if (id) userLabel.set(id, user.displayName ?? user.email ?? id)
    })

    return [...registry.values()]
      .map((watch) => ({
        ...watch,
        owners: watch.ownerUids.map((uid) => ({
          uid,
          label: userLabel.get(uid) ?? uid,
        })),
      }))
      .sort((a, b) => {
        const aTime = toDate(a.lastConnected)?.getTime() ?? 0
        const bTime = toDate(b.lastConnected)?.getTime() ?? 0
        return bTime - aTime
      })
  }, [watchOwnership.data, users.data])

  function handleSortClick(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'identity' ? 'asc' : 'desc')
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          title="Total All Users"
          value={formatCompactNumber(totalUsers.data)}
          description="Semua akun yang terdaftar di aplikasi."
          icon={UserRoundCheck}
        />
        <MetricCard
          title="Users Parlent"
          value={formatCompactNumber(watchOwnership.data?.parlentUserIds.size)}
          description={`Punya minimal satu jam Parlent (${parlentWatchModels.length} model terdaftar). Terisi sejak app mengirim nama jam.`}
          icon={Watch}
          tone="rose"
        />
        <MetricCard
          title="Active Users"
          value={formatCompactNumber(activeUsers.data)}
          description={`Membuka aplikasi dalam ${ACTIVE_WINDOW_DAYS} hari terakhir. Hanya terpantau dari app versi baru.`}
          icon={Smartphone}
          tone="amber"
        />
        <MetricCard
          title="Unit Jam Parlent"
          value={formatCompactNumber(watchOwnership.data?.parlentUnitCount)}
          description="Jumlah jam fisik (MAC unik), bukan jumlah akun."
          icon={Watch}
          tone="teal"
        />
        <MetricCard
          title="Admin Accounts"
          value={formatCompactNumber(totalAdmins.data)}
          description="Akun internal pengelola CMS."
          icon={ShieldCheck}
          tone="emerald"
        />
      </div>

      <div className="rounded-2xl border border-stone-800 bg-stone-900/40 px-5 py-4 text-sm leading-6 text-stone-400">
        <p>
          <span className="font-semibold text-stone-200">Cara menghitung.</span>{' '}
          <span className="text-stone-300">Users Parlent</span> dihitung dari nama jam yang
          dikirim aplikasi saat terhubung. Aplikasi versi lama tidak pernah mengirim nama jam,
          jadi angkanya mulai dari nol dan bertambah setiap user memperbarui aplikasi lalu
          menyambungkan jamnya. Satu user boleh punya beberapa jam — cukup satu jam Parlent
          untuk terhitung.
        </p>
        <p className="mt-2">
          <span className="font-semibold text-stone-200">Unit vs akun.</span> Satu jam fisik bisa
          dipasangkan beberapa akun (unit demo yang berpindah tangan), jadi jumlah akun bisa lebih
          besar dari jumlah unit. Rinciannya ada di Device Registry di bawah.
        </p>
        <p className="mt-2">
          <span className="font-semibold text-stone-200">Kolom Status.</span>{' '}
          <span className="text-stone-300">Belum terpantau</span> berarti aplikasi belum pernah
          melaporkan kapan user membukanya — hampir selalu karena user masih memakai aplikasi versi
          lama. Itu bukan berarti user tidak pernah membuka aplikasi.
        </p>
        <p className="mt-2">
          Saat ini: <span className="text-stone-200">{watchOwnership.data?.parlentUserIds.size ?? 0}</span> pemakai jam Parlent,{' '}
          <span className="text-stone-200">{watchOwnership.data?.otherUserIds.size ?? 0}</span> pemakai jam merek lain,{' '}
          <span className="text-stone-200">{watchOwnership.data?.unknownUserIds.size ?? 0}</span> belum terdeteksi nama jamnya.
        </p>
      </div>

      <SectionCard
        title="User Profiles"
        description="Data utama sinkronisasi mobile app Parlent."
      >
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold uppercase tracking-[0.18em] text-stone-500">Sort by</span>
          {SORT_OPTIONS.map((opt) => {
            const isActive = sortKey === opt.key
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleSortClick(opt.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 font-semibold transition',
                  isActive
                    ? 'border-orange-500/40 bg-orange-500/10 text-orange-300'
                    : 'border-stone-700 bg-stone-900 text-stone-400 hover:border-stone-600 hover:text-stone-200',
                )}
                aria-pressed={isActive}
              >
                {opt.label}
                {isActive && (
                  sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                )}
              </button>
            )
          })}
        </div>
        <DataTable
          rows={sortedUsers}
          pageSize={10}
          columns={[
            {
              header: 'Identity',
              render: (row) => (
                <div>
                  <p className="font-semibold text-white">{row.displayName ?? 'Unnamed user'}</p>
                  <p className="text-xs text-stone-400">{row.email ?? row.id ?? row.uid}</p>
                </div>
              ),
            },
            {
              header: 'Sync',
              render: (row) => (
                <div>
                  <p className="font-medium text-stone-300">{formatRelativeTime(row.lastSync)}</p>
                  <p className="text-xs text-stone-400">Last login: {formatDateTime(row.lastLogin)}</p>
                </div>
              ),
            },
            {
              header: 'Goals',
              render: (row) => {
                const stepGoal = row.dailyStepGoal
                const sleepGoal = row.dailySleepGoal
                return (
                  <div className="text-sm text-stone-400">
                    <p>{stepGoal && stepGoal > 0 ? `${stepGoal.toLocaleString()} steps / day` : <span className="text-stone-600 italic">Not set</span>}</p>
                    <p>{sleepGoal && sleepGoal > 0 ? `${sleepGoal} hrs sleep target` : <span className="text-stone-600 italic">Not set</span>}</p>
                  </div>
                )
              },
            },
            {
              header: 'Watch',
              render: (row) => {
                const userId = row.id ?? row.uid
                const watches = userId ? watchOwnership.data?.byUser.get(userId) : undefined
                return <WatchPills watches={watches ?? []} />
              },
            },
            {
              header: 'Status',
              render: (row) => {
                const status = describeUserActivity(toDate(row.lastOpenedAt), activeSince)
                return (
                  <StatusBadge
                    value={status.label}
                    tone={status.tone}
                    title={status.description}
                  />
                )
              },
            },
            {
              header: 'Action',
              render: (row) => {
                const userId = row.id ?? row.uid
                if (!userId) {
                  return (
                    <span className="inline-flex items-center gap-2 rounded-xl bg-stone-800 px-3 py-2 text-xs font-semibold text-stone-500">
                      <Activity className="h-3.5 w-3.5" />
                      No ID
                    </span>
                  )
                }
                return (
                  <Link
                    to={`/users/${userId}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-500/10 px-3 py-2 text-xs font-semibold text-orange-400 transition hover:bg-orange-500/20"
                  >
                    <Activity className="h-3.5 w-3.5" />
                    View Metrics
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )
              },
            },
          ]}
          emptyTitle="Belum ada user profile"
          emptyDescription="Data user akan muncul di sini setelah tersinkronisasi."
        />
      </SectionCard>

      <SectionCard
        title="Device Registry"
        description="Satu baris per jam fisik (MAC address), lengkap dengan akun yang pernah memakainya."
      >
        <DataTable
          rows={registryRows}
          pageSize={10}
          columns={[
            {
              header: 'Jam',
              render: (row) => (
                <div>
                  <p className="flex items-center gap-2 font-semibold text-white">
                    <Watch className={cn('h-4 w-4', row.isParlent ? 'text-orange-400' : 'text-stone-500')} />
                    {formatWatchLabel(row.name)}
                    {row.isParlent && (
                      <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">
                        Parlent
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-xs text-stone-500">{row.address}</p>
                </div>
              ),
            },
            {
              header: 'Model',
              render: (row) => (
                <div className="text-sm">
                  <p className="text-stone-300">{row.model ?? <span className="italic text-stone-600">Belum terkirim</span>}</p>
                  <p className="text-xs text-stone-500">{row.firmwareVersion ? `FW ${row.firmwareVersion}` : '—'}</p>
                </div>
              ),
            },
            {
              header: 'Dipakai Akun',
              render: (row) => (
                <div className="space-y-1">
                  <p className={cn('text-sm font-semibold', row.owners.length > 1 ? 'text-amber-300' : 'text-stone-300')}>
                    {row.owners.length} akun
                  </p>
                  {row.owners.map((owner) => (
                    <Link
                      key={owner.uid}
                      to={`/users/${owner.uid}`}
                      className="block text-xs text-stone-400 transition hover:text-orange-300"
                    >
                      {owner.label}
                    </Link>
                  ))}
                </div>
              ),
            },
            {
              header: 'Terakhir Terhubung',
              render: (row) => (
                <span className="text-stone-400">{formatRelativeTime(row.lastConnected)}</span>
              ),
            },
          ]}
          emptyTitle="Belum ada device tercatat"
          emptyDescription="Device akan muncul setelah user menyambungkan jamnya dari aplikasi."
        />
      </SectionCard>

      <SectionCard
        title="Admin Access Registry"
        description="Akun internal yang memiliki hak akses ke CMS."
      >
        <DataTable
          rows={admins.data ?? []}
          columns={[
            {
              header: 'Admin',
              render: (row) => (
                <div>
                  <p className="font-semibold text-white">{row.displayName ?? row.uid}</p>
                  <p className="text-xs text-stone-400">{row.email ?? 'No email metadata'}</p>
                </div>
              ),
            },
            {
              header: 'Role',
              render: (row) => <StatusBadge value={row.role} />,
            },
            {
              header: 'Scopes',
              render: (row) => (
                <p className="max-w-xs text-sm leading-6 text-stone-400">
                  {row.scopes?.join(', ') ?? 'global access'}
                </p>
              ),
            },
            {
              header: 'Updated',
              render: (row) => (
                <span className="text-stone-400">{formatDateTime(row.updatedAt)}</span>
              ),
            },
          ]}
          emptyTitle="Belum ada admin registry"
          emptyDescription="Tambahkan admin untuk membatasi akses."
        />
      </SectionCard>

      <SectionCard
        title="Architecture Recommendations"
        description="Integrasi data yang direkomendasikan untuk dashboard ini."
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            'users/{uid}/devices: Data hardware, firmware, dan capability device.',
            'users/{uid}/settings: Konfigurasi notifikasi, bahasa, dan target harian.',
            'users/{uid}/health: Agregasi kesehatan harian dan mingguan.',
            'users/{uid}/syncMeta: Audit log sinkronisasi data dari mobile app.',
          ].map((item) => (
            <div key={item} className="rounded-[24px] border border-stone-800 bg-[#141414] p-4 text-sm leading-6 text-stone-400">
              <UserCog className="mb-3 h-5 w-5 text-orange-500" />
              {item}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  )
}
